/**
 * Turning a filled-in tagging matrix into as few writes as possible.
 *
 * The matrix lets a curator set a value for the whole selection ("every one of
 * these is Power rotation") and then override single cells ("except this one,
 * which is Gold"). The engine takes one value per request, so the plan groups
 * tracks that end up with identical values and sends one request per group --
 * fifty tracks with one exception are two requests, not fifty.
 *
 * **Unchanged is not sent.** A cell whose final value already equals what the
 * track holds is left out. For genre that matters twice over: it is a file write,
 * and rewriting a tag to the value it already has still opens, hashes and saves
 * the file.
 */

export type MatrixColumn = "era" | "energy_level" | "genre" | "rotation";

/** Stored in Nova's custom columns (database), not in the file. */
export const CUSTOM_COLUMNS: { key: Exclude<MatrixColumn, "genre">; label: string; type: "text" | "number" }[] = [
  { key: "era", label: "Era", type: "text" },
  { key: "energy_level", label: "Energy level", type: "number" },
  { key: "rotation", label: "Rotation", type: "text" },
];

/** undefined = leave alone; null = clear; otherwise the value to set. */
export type CellValue = string | number | null | undefined;

export interface MatrixRow {
  trackId: string;
  current: Record<MatrixColumn, string | number | null>;
}

export interface Plan {
  /** One request per distinct set of custom values. */
  custom: { trackIds: string[]; values: Record<string, string | number | null> }[];
  /** One file-tag request per distinct genre. */
  genre: { trackIds: string[]; genre: string | null }[];
  /** Tracks that will change, per column, for the summary. */
  counts: Record<MatrixColumn, number>;
}

function same(a: string | number | null, b: string | number | null): boolean {
  if (a === null || b === null) return a === b;
  return String(a).trim() === String(b).trim();
}

export function finalValue(
  row: MatrixRow,
  column: MatrixColumn,
  bulk: Partial<Record<MatrixColumn, CellValue>>,
  overrides: Record<string, Partial<Record<MatrixColumn, CellValue>>>
): CellValue {
  const override = overrides[row.trackId]?.[column];
  return override !== undefined ? override : bulk[column];
}

export function plan(
  rows: MatrixRow[],
  bulk: Partial<Record<MatrixColumn, CellValue>>,
  overrides: Record<string, Partial<Record<MatrixColumn, CellValue>>>
): Plan {
  const counts: Record<MatrixColumn, number> = { era: 0, energy_level: 0, genre: 0, rotation: 0 };
  const customGroups = new Map<string, { trackIds: string[]; values: Record<string, string | number | null> }>();
  const genreGroups = new Map<string, { trackIds: string[]; genre: string | null }>();

  for (const row of rows) {
    const values: Record<string, string | number | null> = {};
    for (const { key } of CUSTOM_COLUMNS) {
      const next = finalValue(row, key, bulk, overrides);
      if (next === undefined || same(next, row.current[key])) continue;
      values[key] = next;
      counts[key] += 1;
    }
    if (Object.keys(values).length) {
      // Keys sorted so {era, rotation} and {rotation, era} share a group.
      const signature = JSON.stringify(Object.entries(values).sort(([a], [b]) => a.localeCompare(b)));
      const group = customGroups.get(signature) ?? { trackIds: [], values };
      group.trackIds.push(row.trackId);
      customGroups.set(signature, group);
    }

    const genre = finalValue(row, "genre", bulk, overrides);
    if (genre !== undefined && !same(genre, row.current.genre)) {
      const value = genre === null ? null : String(genre).trim() || null;
      const signature = JSON.stringify(value);
      const group = genreGroups.get(signature) ?? { trackIds: [], genre: value };
      group.trackIds.push(row.trackId);
      genreGroups.set(signature, group);
      counts.genre += 1;
    }
  }

  return { custom: [...customGroups.values()], genre: [...genreGroups.values()], counts };
}
